import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import tempTexts from '../store/tempTexts.json';


const InfoSectionWrapper = styled.div`
  cursor: default;
  pointer-events: none;

  position: absolute;
  top: 230px;
  top: calc(14vh + 120px);
  left: 0;
  padding: ${({ vis }) => (vis ? '0.5rem 0 0.1rem 3.4rem' : 0)};
  background-color: rgba(0,0,0,0.4);
  width: ${({ vis }) => (vis ? 100 : 0)}%;
  height: ${({ vis }) => (vis ? 'auto' : '1rem')};
  max-width: 700px;

  color: white;
  font-family: "Lusitana", serif;
  font-size: 2rem;
  line-height: 2.2rem;
  font-weight: 400;
  text-shadow: -1px -1px 2px #000,
    1px -1px 2px #000,
    -1px 1px 2px #000,
    1px 1px 2px #000,
    -1px -1px 2px #000,
    1px -1px 2px #000,
    -1px 1px 2px #000,
    1px 1px 2px #000;

  @media only screen and (max-width: 1200px) {
    font-size: 20px;
    line-height: 24px;
    max-width: 450px;
  }

  @media only screen and (max-height: 900px) {
    font-size: 20px;
    line-height: 22px;
    max-width: 450px;
  }

  @media only screen and (max-height: 479px),
    only screen and (max-width: 399px) {
      top: calc(14vh + 80px);
      font-size: 1rem;
      line-height: 1.2rem;
  }
`;

const InfoContent = styled.div`
  overflow: hidden;
  transition: all 200ms;
  opacity: ${({ vis }) => (vis ? 1 : 0)};
`;

const Line = styled.p`
  padding: 0 0 13px 0;
  margin: 0;

  @media only screen and (max-width: 1200px) {
    padding: 0 0 4px 0;
  }

  @media only screen and (max-height: 900px) {
    padding: 0 0 4px 0;
  }
`;

const InfoSectionActive = styled.span`
  cursor: default;
  font-weight: bold;
  white-space: nowrap;
  

  @media only screen and (max-width: 750px) {
    white-space: normal;
  }
`;

const NumberValue = styled.span`
  width: 69px;
  display: inline-block;

  @media only screen and (max-width: 1200px) {
    width: 62px;
  }

  @media only screen and (max-width: 900px) {
    width: 38px;
  }

  @media only screen and (max-height: 900px) {
    width: 38px;
  }

  @media only screen and (max-height: 479px),
    only screen and (max-width: 399px) {
      width: 2.1em;
  }
`;

const ShowHideButton = styled.button`
  height: 3.6rem;
  width: 3rem;
  background-color: rgba(0, 0, 0, 0.0);
  border-radius: 2px;
  pointer-events: all;
  border: 0;
  font-size: 22px;
  color: #fff;

  cursor: pointer;
  position: absolute;
  left: 0;
  top: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }

  &:after {
    content: "";
    position: absolute;
    width: 1rem;
    height: 1rem;
    transition: border-color 140ms;
    top: 1.2rem;

    ${({ vis }) => (vis ? `
      border: solid #ffd6b6;
      border-width: 0 0 4px 4px;
      left: 20px;
      ` : `
      border: solid #fff;
      border-width: 4px 4px 0 0;
      left: 10px;
    `)}

    display: inline-block;
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
  }
`;

const humanify = (n) => {
  if (n / 1000000 > 10) {
    return `${Math.floor(n / 1000000)} million`;
  }

  if (n / 1000000 > 0.1) {
    return `${(n / 1000000).toFixed(1)} million`;
  }

  if (n / 1000 > 1) {
    return `${(n / 1000).toFixed(1)} thousand`;
  }

  return Math.floor(n);
};

/**
 * Match years with temperature cutoff points to retrieve
 * warming descriptions (workable proxy solution since there
 * is only one warming scenario). Values larger than last year
 * in list return last text.
 */

const findTempKey = (n) => {
  const yrs = [2012, 2028, 2041, 2065, 2085, 2108, 2300];
  const tempPts = ['1.0', '1.5', '2.0', '3.0', '4.0', '5.0', '8.0'];
  if (n > yrs[yrs.length - 1]) {
    return tempPts[yrs.length - 1];
  }
  return tempPts[yrs.findIndex((val) => val >= n)];
};


/**
 * Info section React Component.
 */
class InfoSection extends PureComponent {
  static propTypes = {
    address: PropTypes.string.isRequired,
    altitude: PropTypes.number.isRequired,
    elevation: PropTypes.number.isRequired,
    isMapUi: PropTypes.bool.isRequired,
    isAvailable: PropTypes.bool.isRequired,
    population: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
  };

  /**
   * @param {!props} props properties of the class.
   */
  constructor(props) {
    super(props);

    this.state = { isVisible: true };

    this.toggle = this.toggle.bind(this);
  }

  /**
   * React Lifecycle method.
   */
  componentDidUpdate() {
    const {
      altitude,
      elevation,
      isAvailable,
      isMapUi,
    } = this.props;

    const { isVisible } = this.state;

    if (!isMapUi && elevation >= altitude && !isVisible && isAvailable) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isVisible: true });
    }
  }

  /**
   * Get info address if it is available.
   * @return {jsx|null} Info address in jsx.
   */
  getAddress() {
    const { address } = this.props;

    if (!address) {
      return null;
    }

    return (
      <span key="address">
        <InfoSectionActive>
          {`${address}.`}
        </InfoSectionActive>
        {' '}
      </span>
    );
  }

  /**
   * Toggle visibility of the component.
   */
  toggle() {
    const { isVisible } = this.state;
    this.setState({ isVisible: !isVisible });
  }

  /**
   * React Lifecycle method.
   * @return {jsx} jsx representation of the component.
   */
  render() {
    const { isVisible } = this.state;
    const {
      altitude,
      elevation,
      isMapUi,
      population,
      isAvailable,
      year,
    } = this.props;


    const isOpen = isVisible && isAvailable;


    const seaLevelContextText = year > 2300
      ? (
        <Line key="infotext-rest">
          Collapsing ice sheets raise sea levels
          <InfoSectionActive>
            <NumberValue>
              &nbsp;
              {` ${altitude.toFixed(1)} `}
            </NumberValue>
          </InfoSectionActive>
          meters&nbsp;since 2000.
        </Line>
      )
      : (
        <Line key="infotext-rest">
          The global average sea level has risen
          <InfoSectionActive>
            <NumberValue>
              &nbsp;
              {` ${altitude.toFixed(1)} `}
            </NumberValue>
          </InfoSectionActive>
          meters since 2000.
        </Line>

      );


    const seaLeveText = altitude <= 0 ? null
    : seaLevelContextText;


    const endingText = isMapUi
      ? ' in the selected area'
      : ' within a 5km radius';

    const populationText = population < 0 ? null : (
      <Line>
        Estimated
        &nbsp;
        {humanify(population)}
        &nbsp;
        people have been forced to relocate from their homes
        {endingText}
        .
      </Line>
    );


    const warmingText = year < 2013 ? null : (
      <Line>
        {`${tempTexts[findTempKey(year)]}`}
      </Line>
    );

    const nothingToSeeHere = !isMapUi && elevation >= altitude
      ? <Line>Location is still above sea level.</Line>
      : null;

    return (
      <InfoSectionWrapper vis={isOpen}>
        <InfoContent vis={isOpen}>

          {year > 2300
            ? (
              <Line>
                {this.getAddress()}
                No reliable estimates exist beyond 2300.
              </Line>
            ) : (
              <Line>
                {this.getAddress()}
                The year is
                <InfoSectionActive key="infotext-year">
                  {` ${year.toFixed(0)}.`}
                </InfoSectionActive>
              </Line>
            )}
          {nothingToSeeHere}
          {nothingToSeeHere ? null : seaLeveText}
          {warmingText}
          {nothingToSeeHere ? null : populationText}
        </InfoContent>

        {
          nothingToSeeHere || !isAvailable ? null : (
            <ShowHideButton
              id="toggle-info-text"
              aria-label="Toggle info text"
              onClick={this.toggle}
              vis={isOpen}
            />
          )
        }
      </InfoSectionWrapper>
    );
  }
}

export default connect((state) => {
  const {
    altitudes,
    map,
    panorama,
    ui,
  } = state;
  const { address, population, elevation } = map;

  return {
    address,
    altitude: altitudes.value,
    elevation,
    isMapUi: ui.isMapUi,
    isAvailable: !!(panorama.id || ui.isMapUi),
    population,
    year: altitudes.year,
  };
})(InfoSection);
